// Comprehensive traffic source detection with 50+ sources

// Ad platform click ID parameters
const AD_CLICK_IDS = {
  gclid: 'Google Ads',
  fbclid: 'Facebook Ads',
  msclkid: 'Microsoft Ads',
  ttclid: 'TikTok Ads',
  li_fat_id: 'LinkedIn Ads',
  twclid: 'Twitter Ads',
  pclid: 'Pinterest Ads',
  sclid: 'Snapchat Ads',
  rdt_cid: 'Reddit Ads',
  dclid: 'Google Display',
  wbraid: 'Google Ads (iOS)',
  gbraid: 'Google Ads (App)',
} as const;

// Search engines
const SEARCH_ENGINES = [
  { patterns: ['google.', 'google.com'], name: 'Google' },
  { patterns: ['bing.com', 'bing.'], name: 'Bing' },
  { patterns: ['yahoo.com', 'yahoo.', 'search.yahoo'], name: 'Yahoo' },
  { patterns: ['duckduckgo.com', 'duckduckgo.'], name: 'DuckDuckGo' },
  { patterns: ['baidu.com', 'baidu.'], name: 'Baidu' },
  { patterns: ['yandex.', 'yandex.com'], name: 'Yandex' },
  { patterns: ['ask.com'], name: 'Ask' },
  { patterns: ['ecosia.org'], name: 'Ecosia' },
  { patterns: ['qwant.com'], name: 'Qwant' },
  { patterns: ['startpage.com'], name: 'Startpage' },
  { patterns: ['brave.com/search'], name: 'Brave Search' },
  { patterns: ['neeva.com'], name: 'Neeva' },
  { patterns: ['sogou.com'], name: 'Sogou' },
  { patterns: ['naver.com'], name: 'Naver' },
  { patterns: ['daum.net'], name: 'Daum' },
];

// Social networks with all possible referrer domains
const SOCIAL_NETWORKS = [
  { patterns: ['facebook.com', 'fb.com', 'fb.me', 'fbcdn.net', 'l.facebook.com', 'lm.facebook.com', 'm.facebook.com'], name: 'Facebook' },
  { patterns: ['instagram.com', 'l.instagram.com', 'ig.me'], name: 'Instagram' },
  { patterns: ['twitter.com', 't.co', 'x.com', 'mobile.twitter.com'], name: 'X (Twitter)' },
  { patterns: ['linkedin.com', 'lnkd.in', 'linkedin.com/feed'], name: 'LinkedIn' },
  { patterns: ['tiktok.com', 'vm.tiktok.com', 't.tiktok.com'], name: 'TikTok' },
  { patterns: ['pinterest.com', 'pin.it', 'pinterest.'], name: 'Pinterest' },
  { patterns: ['youtube.com', 'youtu.be', 'm.youtube.com', 'youtube-nocookie.com'], name: 'YouTube' },
  { patterns: ['reddit.com', 'redd.it', 'old.reddit.com', 'new.reddit.com', 'i.reddit.com'], name: 'Reddit' },
  { patterns: ['snapchat.com', 'snap.com', 'sc.lnk'], name: 'Snapchat' },
  { patterns: ['whatsapp.com', 'wa.me', 'api.whatsapp.com', 'web.whatsapp.com'], name: 'WhatsApp' },
  { patterns: ['telegram.org', 't.me', 'telegram.me'], name: 'Telegram' },
  { patterns: ['discord.com', 'discord.gg', 'discordapp.com'], name: 'Discord' },
  { patterns: ['twitch.tv', 'twitch.com'], name: 'Twitch' },
  { patterns: ['threads.net'], name: 'Threads' },
  { patterns: ['mastodon.', 'mastodon.social'], name: 'Mastodon' },
  { patterns: ['tumblr.com'], name: 'Tumblr' },
  { patterns: ['vk.com', 'vkontakte.ru'], name: 'VK' },
  { patterns: ['weibo.com', 'weibo.cn'], name: 'Weibo' },
  { patterns: ['wechat.com', 'weixin.qq.com'], name: 'WeChat' },
  { patterns: ['quora.com'], name: 'Quora' },
  { patterns: ['medium.com'], name: 'Medium' },
  { patterns: ['clubhouse.com'], name: 'Clubhouse' },
  { patterns: ['bluesky.', 'bsky.app'], name: 'Bluesky' },
];

// Email clients
const EMAIL_CLIENTS = [
  { patterns: ['mail.google.com', 'googlemail.com'], name: 'Gmail' },
  { patterns: ['outlook.live.com', 'outlook.office.com', 'outlook.office365.com'], name: 'Outlook' },
  { patterns: ['mail.yahoo.com'], name: 'Yahoo Mail' },
  { patterns: ['mail.aol.com'], name: 'AOL Mail' },
  { patterns: ['protonmail.com', 'proton.me'], name: 'ProtonMail' },
  { patterns: ['icloud.com', 'mail.me.com'], name: 'Apple Mail' },
  { patterns: ['mailchimp.com', 'list-manage.com', 'campaign-archive.com'], name: 'Mailchimp' },
  { patterns: ['hubspot.com', 'hs-email'], name: 'HubSpot Email' },
  { patterns: ['klaviyo.com'], name: 'Klaviyo' },
  { patterns: ['sendgrid.net', 'sendgrid.com'], name: 'SendGrid' },
  { patterns: ['constantcontact.com'], name: 'Constant Contact' },
  { patterns: ['sendinblue.com', 'brevo.com'], name: 'Brevo' },
  { patterns: ['activecampaign.com'], name: 'ActiveCampaign' },
];

// News & content platforms
const NEWS_CONTENT = [
  { patterns: ['news.google.com'], name: 'Google News' },
  { patterns: ['news.ycombinator.com', 'hn.algolia.com'], name: 'Hacker News' },
  { patterns: ['producthunt.com'], name: 'Product Hunt' },
  { patterns: ['indiehackers.com'], name: 'Indie Hackers' },
  { patterns: ['dev.to'], name: 'Dev.to' },
  { patterns: ['hashnode.com'], name: 'Hashnode' },
  { patterns: ['pocket.com', 'getpocket.com'], name: 'Pocket' },
  { patterns: ['flipboard.com'], name: 'Flipboard' },
  { patterns: ['feedly.com'], name: 'Feedly' },
];

// Productivity & docs
const PRODUCTIVITY = [
  { patterns: ['notion.so', 'notion.site'], name: 'Notion' },
  { patterns: ['docs.google.com', 'drive.google.com'], name: 'Google Docs' },
  { patterns: ['slack.com'], name: 'Slack' },
  { patterns: ['teams.microsoft.com', 'teams.live.com'], name: 'Microsoft Teams' },
  { patterns: ['trello.com'], name: 'Trello' },
  { patterns: ['asana.com'], name: 'Asana' },
  { patterns: ['monday.com'], name: 'Monday.com' },
  { patterns: ['clickup.com'], name: 'ClickUp' },
  { patterns: ['basecamp.com'], name: 'Basecamp' },
  { patterns: ['airtable.com'], name: 'Airtable' },
  { patterns: ['figma.com'], name: 'Figma' },
  { patterns: ['miro.com'], name: 'Miro' },
];

// Review sites
const REVIEW_SITES = [
  { patterns: ['g2.com', 'g2crowd.com'], name: 'G2' },
  { patterns: ['capterra.com'], name: 'Capterra' },
  { patterns: ['trustpilot.com'], name: 'Trustpilot' },
  { patterns: ['getapp.com'], name: 'GetApp' },
  { patterns: ['softwareadvice.com'], name: 'Software Advice' },
  { patterns: ['trustradius.com'], name: 'TrustRadius' },
  { patterns: ['alternativeto.net'], name: 'AlternativeTo' },
];

export interface TrafficSourceResult {
  source: string;
  medium: string;
  category: 'search' | 'social' | 'email' | 'referral' | 'paid' | 'direct' | 'news' | 'productivity' | 'review' | 'affiliate' | 'partner';
  adPlatform?: string;
  isOrganic: boolean;
  isPaid: boolean;
  clickId?: {
    type: string;
    value: string;
  };
}

export function detectAdPlatform(searchParams: string): { platform: string; clickId: string } | null {
  const params = new URLSearchParams(searchParams);
  
  for (const [param, platform] of Object.entries(AD_CLICK_IDS)) {
    const value = params.get(param);
    if (value) {
      return { platform, clickId: value };
    }
  }
  
  return null;
}

export function getPartnerFromURL(): string | null {
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  
  // Check various partner/affiliate parameters
  return params.get('partner') || 
         params.get('ref') || 
         params.get('affiliate') || 
         params.get('aff') ||
         params.get('referrer') ||
         params.get('via') ||
         null;
}

export function getClickIds(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const clickIds: Record<string, string> = {};
  
  for (const param of Object.keys(AD_CLICK_IDS)) {
    const value = params.get(param);
    if (value) {
      clickIds[param] = value;
    }
  }
  
  return clickIds;
}

function matchPatterns(hostname: string, patterns: string[]): boolean {
  const lowerHostname = hostname.toLowerCase();
  return patterns.some(pattern => lowerHostname.includes(pattern.toLowerCase()));
}

function findSource(hostname: string, sources: Array<{ patterns: string[]; name: string }>): string | null {
  for (const source of sources) {
    if (matchPatterns(hostname, source.patterns)) {
      return source.name;
    }
  }
  return null;
}

export function parseTrafficSource(referrer: string, searchParams?: string): TrafficSourceResult {
  // Default direct traffic
  const defaultResult: TrafficSourceResult = {
    source: 'Direct',
    medium: 'none',
    category: 'direct',
    isOrganic: false,
    isPaid: false,
  };

  // Check for ad click IDs first (highest priority - paid traffic)
  if (searchParams) {
    const adInfo = detectAdPlatform(searchParams);
    if (adInfo) {
      return {
        source: adInfo.platform,
        medium: 'cpc',
        category: 'paid',
        adPlatform: adInfo.platform,
        isOrganic: false,
        isPaid: true,
        clickId: {
          type: Object.entries(AD_CLICK_IDS).find(([_, v]) => v === adInfo.platform)?.[0] || 'unknown',
          value: adInfo.clickId,
        },
      };
    }
  }

  // Check for partner/affiliate
  const partnerId = getPartnerFromURL();
  if (partnerId) {
    return {
      source: `Partner: ${partnerId}`,
      medium: 'affiliate',
      category: 'partner',
      isOrganic: false,
      isPaid: false,
    };
  }

  // No referrer = direct
  if (!referrer) {
    return defaultResult;
  }

  let hostname: string;
  try {
    hostname = new URL(referrer).hostname;
  } catch {
    return defaultResult;
  }

  // Check each source category
  const searchEngine = findSource(hostname, SEARCH_ENGINES);
  if (searchEngine) {
    return {
      source: searchEngine,
      medium: 'organic',
      category: 'search',
      isOrganic: true,
      isPaid: false,
    };
  }

  const socialNetwork = findSource(hostname, SOCIAL_NETWORKS);
  if (socialNetwork) {
    return {
      source: socialNetwork,
      medium: 'social',
      category: 'social',
      isOrganic: true,
      isPaid: false,
    };
  }

  const emailClient = findSource(hostname, EMAIL_CLIENTS);
  if (emailClient) {
    return {
      source: emailClient,
      medium: 'email',
      category: 'email',
      isOrganic: false,
      isPaid: false,
    };
  }

  const newsSource = findSource(hostname, NEWS_CONTENT);
  if (newsSource) {
    return {
      source: newsSource,
      medium: 'referral',
      category: 'news',
      isOrganic: true,
      isPaid: false,
    };
  }

  const productivitySource = findSource(hostname, PRODUCTIVITY);
  if (productivitySource) {
    return {
      source: productivitySource,
      medium: 'referral',
      category: 'productivity',
      isOrganic: true,
      isPaid: false,
    };
  }

  const reviewSite = findSource(hostname, REVIEW_SITES);
  if (reviewSite) {
    return {
      source: reviewSite,
      medium: 'referral',
      category: 'review',
      isOrganic: true,
      isPaid: false,
    };
  }

  // Generic referral
  return {
    source: hostname,
    medium: 'referral',
    category: 'referral',
    isOrganic: true,
    isPaid: false,
  };
}

export function getEnhancedUTMParams(): {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  utm_id: string | null;
} {
  if (typeof window === 'undefined') {
    return { utm_source: null, utm_medium: null, utm_campaign: null, utm_term: null, utm_content: null, utm_id: null };
  }
  
  const params = new URLSearchParams(window.location.search);
  
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
    utm_id: params.get('utm_id'),
  };
}

export function getDeviceInfo(): {
  language: string;
  timezone: string;
  viewportSize: string;
  connectionType: string | null;
  isNewVisitor: boolean;
} {
  if (typeof window === 'undefined') {
    return {
      language: 'unknown',
      timezone: 'unknown',
      viewportSize: 'unknown',
      connectionType: null,
      isNewVisitor: true,
    };
  }

  const isNewVisitor = !localStorage.getItem('xpand_returning_visitor');
  if (!isNewVisitor) {
    // Mark as returning for next visit
    localStorage.setItem('xpand_returning_visitor', 'true');
  }

  return {
    language: navigator.language || 'unknown',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    connectionType: (navigator as Navigator & { connection?: { effectiveType?: string } }).connection?.effectiveType || null,
    isNewVisitor,
  };
}

export function getPerformanceMetrics(): {
  loadTime: number | null;
  ttfb: number | null;
  fcp: number | null;
  domInteractive: number | null;
  domComplete: number | null;
} {
  if (typeof window === 'undefined' || !window.performance) {
    return { loadTime: null, ttfb: null, fcp: null, domInteractive: null, domComplete: null };
  }

  const timing = performance.timing;
  const navigationStart = timing.navigationStart;

  // Get FCP from PerformanceObserver entries
  let fcp: number | null = null;
  const paintEntries = performance.getEntriesByType('paint');
  const fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');
  if (fcpEntry) {
    fcp = Math.round(fcpEntry.startTime);
  }

  return {
    loadTime: timing.loadEventEnd > 0 ? timing.loadEventEnd - navigationStart : null,
    ttfb: timing.responseStart > 0 ? timing.responseStart - navigationStart : null,
    fcp,
    domInteractive: timing.domInteractive > 0 ? timing.domInteractive - navigationStart : null,
    domComplete: timing.domComplete > 0 ? timing.domComplete - navigationStart : null,
  };
}
